import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { generatePublishOfferClickAction, openTopUpBalance } from '../../actions/webview';
import { selectAnalyticsOfferStatus, selectAnalyticsProduct } from '../../selectors/analytics';
import { selectCurrentBet } from '../../selectors/auction';
import { selectBetPosition } from '../../selectors/betPosition';
import {
  selectTotalAmount,
  selectIsPublishButtonPending,
  selectPublicationButtonText,
  selectIsPublishButtonDisabled,
  selectIsAutoProlongChecked,
} from '../../selectors/checkoutInfo';
import { selectLayoutDeviceType } from '../../selectors/layout';
import { selectOfferId } from '../../selectors/offer';
import { selectIsPublishToHiddenBase, selectShouldPayByCard } from '../../selectors/publishAvailable';
import { selectActiveServiceId } from '../../selectors/publishTerms';
import { setRecommendedPackageCheckoutFillBalance } from '../../actions/recommendedPackages';
import { selectCanFillBalance, selectIsSubagentMasterAccountMoneyLimitExceeded } from '../../selectors/balance';
import { setIsSubagentNotEnoughMoney } from '../../actions/publishInfo';
import { publishOffer } from '../../thunks/offer/publishOffer';
import { setPlacingMoneySource } from '../../actions/publishTerms';
import { EMoneySource } from '../../types/reducers/user';
import { loadPublishTerms } from '../../thunks/publishTerms/loadPublishTerms';
import { TDispatch } from '../../../common/types/dispatch';
import { getAnalyticsSiteType } from '../../utils/analytics';
import { useAppContext } from '../../../AppModulesWrapper';
import { PublishButton } from './components/PublishButton';
import { SelfMoneySourceButton } from './components/SelfMoneySourceButton';
import { trackPayByCardModalOpen, trackPublishByMoneyFailed, trackPublishClick } from './tracking';

export const PublishButtonContainer = () => {
  const { webview } = useAppContext();

  const totalPrice = useSelector(selectTotalAmount);
  const isPending = useSelector(selectIsPublishButtonPending);
  const isPublishToHiddenBase = useSelector(selectIsPublishToHiddenBase);
  const analyticsOfferStatus = useSelector(selectAnalyticsOfferStatus);
  const text = useSelector(selectPublicationButtonText);
  const shouldPayByCard = useSelector(selectShouldPayByCard);
  const offerId = useSelector(selectOfferId);
  const isDisabled = useSelector(selectIsPublishButtonDisabled);
  const currentBet = useSelector(selectCurrentBet);
  const analyticsProduct = useSelector(selectAnalyticsProduct);
  const betPosition = useSelector(selectBetPosition);
  const selectedServiceId = useSelector(selectActiveServiceId)!;
  const isAutoprolong = useSelector(selectIsAutoProlongChecked);
  const deviceType = useSelector(selectLayoutDeviceType);
  const isSubagentMasterAccountMoneyLimitExceeded = useSelector(selectIsSubagentMasterAccountMoneyLimitExceeded);
  const canFillBalance = useSelector(selectCanFillBalance);

  const { offerPosition = null, page = null } = betPosition || {};

  const siteType = getAnalyticsSiteType(deviceType);

  const dispatch = useDispatch<TDispatch>();

  const handleSelfMoneySourceButtonClick = React.useCallback(() => {
    dispatch(setPlacingMoneySource(EMoneySource.Self));
    dispatch(loadPublishTerms());
  }, [dispatch]);

  const openBalanceWidget = React.useCallback(() => {
    if (!canFillBalance) {
      dispatch(setIsSubagentNotEnoughMoney(true));
    } else {
      dispatch(setRecommendedPackageCheckoutFillBalance(false));
      webview.sendAction(openTopUpBalance(totalPrice.price));
    }
  }, [dispatch, canFillBalance, totalPrice, webview]);

  const handlePublishButtonClick = React.useCallback(() => {
    webview.sendAction(generatePublishOfferClickAction(isDisabled));

    if (isDisabled) {
      return;
    }

    trackPublishClick({
      analyticsOfferStatus,
      analyticsProduct,
      totalPrice: totalPrice.price,
      currentBet,
      selectedServiceId,
      offerPosition,
      page,
      isAutoprolong,
    });

    if (shouldPayByCard) {
      trackPayByCardModalOpen(analyticsOfferStatus);
      trackPublishByMoneyFailed({ offerId, siteType, analyticsOfferStatus });

      openBalanceWidget();
    } else {
      dispatch(publishOffer());
    }
  }, [
    dispatch,
    isPublishToHiddenBase,
    totalPrice,
    analyticsOfferStatus,
    siteType,
    shouldPayByCard,
    offerId,
    isDisabled,
    currentBet,
    selectedServiceId,
    offerPosition,
    page,
    isAutoprolong,
  ]);

  return isSubagentMasterAccountMoneyLimitExceeded ? (
    <SelfMoneySourceButton handleButtonClick={handleSelfMoneySourceButtonClick} isPending={isPending} />
  ) : (
    <PublishButton text={text} handleButtonClick={handlePublishButtonClick} isPending={isPending} />
  );
};
